import React, { FC, useState, useEffect, useRef, useCallback } from 'react';
import { AuthState } from 'careand-redux/slices/auth';
import { RootState, useAppSelector } from 'careand-redux/store';
import { IsRefillRequestsEnabled } from 'careand-redux/selectors';
import { PhysicianImage } from 'components/Common/UserImage';
import { usePathParams } from 'components/Navigation/NavStack';
import DefaultButton from 'components/Utils/NewDefaultButton';
import Icon from 'components/Utils/NewIcon';
import {
  AppointmentNote,
  AppointmentNoteItemTypeEnum,
  Immunization,
  PatientDocument,
  Prescription,
  PrescriptionMedication,
  PrescriptionRefillStatusEnum,
} from 'generated/api';
import moment from 'moment-timezone';
import TopBar from 'scenes/HomePatient2/TopBar';
import DocumentUtils from 'services/DocumentUtils';
import PhysicianUtils from 'services/PhysicianUtils';
import './style.scss';
import { detectReportVariant, fetchReportCss } from 'services/ReportCssUtils';
import { MarkRead } from 'hoc/markUnread';
import SubscriptionUtils from 'services/SubscriptionUtils';
import DefaultPatientModal from 'components/Utils/DefaultPatientModal';
import PrescriptionRefill from 'components/HealthRecords/Prescriptions/PrescriptionRefill';
import { api } from 'careand-redux/api/ApiThunks';
import { downloadBlob } from 'services/FileDownloadUtils';
import DefaultModal from 'components/Utils/DefaultModal';
import LargeResourcePreview from 'components/Utils/LargeResourcePreview';
import { DispatchOptions, useAppDispatch } from 'careand-redux/useAppDispatch';
import { useNavStack } from 'careand-redux/slices/ui/hooks';
import { Stacks } from 'careand-redux/slices/ui/navigation';
import { Device } from 'services/Native/Device';
import CalendarUtils from 'services/CalendarUtils';
import CPPUtils from 'services/CPPUtils';
import DiagnosticResults from 'components/HealthRecords/DiagnosticResults';
import { PharmacyBadge } from 'components/Pharmacy/PharmacyBadge';

const Detail: FC<{ type?: string; id?: string; hideBar?: boolean }> = ({
  type,
  id,
  hideBar = false,
}) => {
  const params = usePathParams() as { type: string; id: string; patientId: string };
  const activeUserId = useAppSelector((state) => state.authentication.activeUserId);
  const patientId = params.patientId || activeUserId;

  let finalType = type || params.type;
  const finalId = id || params.id;

  const record = useAppSelector((state) => selectRecord(state, finalType, finalId, patientId!));

  const title = getTitle(finalType, record);

  if (!record) {
    return null;
  }

  return (
    <div className="page hr-detail-page">
      {!hideBar && <TopBar title={title} />}
      <div className="page-content">
        <div className="content-wrapper">
          {finalType === 'prescriptions' && (
            <MarkRead item={record} type="Prescription">
              <PrescriptionItems prescription={record as Prescription} />
            </MarkRead>
          )}
          {finalType === 'immunizations' && (
            <MarkRead item={record} type="Immunization">
              <ImmunizationItems immunization={record as Immunization} />
            </MarkRead>
          )}
          {finalType === 'notes' && (
            <MarkRead item={record} type="AppointmentNote">
              <AppointmentNoteItems note={record as AppointmentNote} />
            </MarkRead>
          )}
          {finalType === 'documents' && (
            <MarkRead item={record} type="PatientDocument">
              <DocumentItems document={record as PatientDocument} />
            </MarkRead>
          )}
        </div>
      </div>
    </div>
  );
};

export default Detail;

function getTitle(type: string, record: unknown | undefined) {
  if (type === 'documents') {
    return (record as PatientDocument)?.document_type ?? 'Document';
  }
  if (type === 'notes') {
    return 'Appointment Note';
  }
  if (type === 'results') {
    return 'Result';
  }
  if (type === 'referrals') {
    return 'Referral';
  }
  if (type === 'prescriptions') {
    return 'Prescription';
  }
  if (type === 'immunizations') {
    return 'Immunization';
  }

  return '';
}

function selectRecord(state: RootState, type: string, id: string, patientId: string) {
  if (!patientId) {
    return;
  }

  const hr = state.healthRecords.byId[patientId];

  if (type === 'documents') {
    return hr?.documents?.find((it) => it.id === id);
  }

  if (type === 'prescriptions') {
    return hr?.prescriptions?.find((it) => it.id === id);
  }

  if (type === 'immunizations') {
    return hr?.immunizations.find((it) => it.id === id);
  }

  if (type === 'notes') {
    return hr?.notes.find((it) => it.id === id);
  }
}

const Header = ({
  practitionerId,
  date,
  filename,
  children,
}: {
  practitionerId?: string;
  date?: number;
  filename?: string | string[];
  children?: React.ReactNode;
}) => {
  const practitioner = useAppSelector((state) => state.physicians.byId[practitionerId ?? '-']);

  return (
    <div className="hr-detail-card">
      {practitioner && (
        <div className="hr-detail-card__section">
          <span>Practitioner</span>
          <div className="practitioner-badge">
            <PhysicianImage user={practitioner} size={30} />
            <span>{PhysicianUtils.getName(practitioner)}</span>
          </div>
        </div>
      )}
      {date && (
        <div className="hr-detail-card__section">
          <span>Date</span>
          <span className="hr-detail-card__text">
            {CalendarUtils.format(date, 'hh:mm A YYYY-MM-DD')}
          </span>
        </div>
      )}
      {filename && (
        <div className="hr-detail-card__section">
          {typeof filename === 'string' && <AttachmentBadge name={filename} type="pdf" />}
          {Array.isArray(filename) &&
            filename.map((it) => <AttachmentBadge name={it} key={it} type="pdf" />)}
        </div>
      )}
      {children}
    </div>
  );
};

export const PrescriptionItems = ({ prescription }: { prescription: Prescription }) => {
  const refill = useAppSelector(
    (state) =>
      state.healthRecords.byId[prescription?.patient_id]?.refills.find(
        (it) => it.prescription_id === prescription?.id,
      ),
  );
  const subscription = useAppSelector(
    (state) => state.payments.subscriptions[prescription?.patient_id],
  );
  const [refillModal, setRefillModal] = useState(false);
  const filename = prescription.attachments?.[0];
  const [isLoading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const dispatch = useAppDispatch();
  const userRole = useAppSelector((state) => state.authentication.role);
  const refillRequestsEnabled = useAppSelector(IsRefillRequestsEnabled);

  if (!prescription) return null;

  const canRequestRefill = SubscriptionUtils.isActivePayingSubscription(subscription) && !refill;

  const refillButtonTitle = {
    [PrescriptionRefillStatusEnum.Completed]: 'Your refill request is completed',
    [PrescriptionRefillStatusEnum.Pending]: 'Your refill request is pending',
    [PrescriptionRefillStatusEnum.Rejected]: 'Your refill request was rejected',
    NONE: 'Request a Refill',
  }[refill?.status ?? 'NONE'];

  const onClickOpenPrescription = () => {
    if (!filename) {
      return;
    }
    setLoading(true);

    downloadBlob(
      api.HealthRecords.getPrescriptionPDF,
      {
        patientId: prescription.patient_id,
        prescriptionId: prescription.id!,
      },
      {
        filename: filename,
      },
    ).finally(() => setLoading(false));
  };

  function onClickOpenPrescriptionDesktop() {
    setShowModal(true);
  }

  function getFile<T extends DispatchOptions>(dispatchOptions: T) {
    return dispatch(
      api.HealthRecords.getPrescriptionPDF({
        patientId: prescription.patient_id,
        prescriptionId: prescription.id!,
      }),
      dispatchOptions,
    );
  }

  return (
    <>
      <Header
        practitionerId={prescription.physician_id}
        date={prescription.date}
        filename={prescription.attachments && 'Prescription'}
      >
        {prescription.pharmacy && (
          <>
            <div className="hr-detail-card__section">
              <span>Pharmacy</span>
            </div>
            <PharmacyBadge pharmacy={prescription.pharmacy} />
          </>
        )}
      </Header>

      {prescription.medications.map((it) => (
        <MedicationItems prescription={prescription} medication={it} key={it.id} />
      ))}

      {userRole === 'patient' && (
        <div className="buttons">
          <div className="mobile-only">
            <DefaultButton
              name="Download Prescription"
              icon="file-pdf"
              onClick={onClickOpenPrescription}
              loading={isLoading}
            />
          </div>
          <div className="desktop-only">
            <DefaultButton
              name="Open Prescription"
              icon="file-pdf"
              onClick={onClickOpenPrescriptionDesktop}
              loading={isLoading}
            />
          </div>
          {refillRequestsEnabled && (
            <DefaultButton
              className="light"
              name={refillButtonTitle}
              disabled={!canRequestRefill}
              onClick={() => setRefillModal(true)}
            />
          )}
        </div>
      )}
      {refillModal && (
        <DefaultPatientModal
          className="modal prescription-refill-modal"
          title="Prescription Refill"
          wrap={true}
          padding={true}
          content={
            <PrescriptionRefill
              prescription={prescription}
              onSuccess={() => setRefillModal(false)}
            />
          }
          isOpen={refillModal}
          onRequestClose={() => setRefillModal(false)}
        />
      )}
      {showModal && (
        <DefaultModal
          className="modal health-record-modal large-modal"
          wrap={true}
          padding={false}
          content={<LargeResourcePreview autoLoad={true} getFile={getFile} />}
          isOpen={showModal}
          onRequestClose={() => setShowModal(false)}
        />
      )}
    </>
  );
};

const MedicationItems = ({
  medication,
  prescription,
}: {
  medication: PrescriptionMedication;
  prescription: Prescription;
}) => {
  const workingMedication: PrescriptionMedication =
    CPPUtils.convertMedicationDosingInformation(medication);

  const { name, refills, comments, quantity, quantity_unit } = medication;

  const lastsUntil = CPPUtils.calculateDrugLastsUntil(workingMedication, prescription.date);

  function printDosingInstructions(medication: PrescriptionMedication) {
    const { route, dosing_instructions = [] } = medication;

    return (
      <div className="hr-detail-card__subsection is-multiline">
        <span>Dosing Instructions</span>
        {dosing_instructions.map((value, index) => {
          let { dosage, dosage_unit, frequency, time, time_unit, connector } = value;
          return (
            <span className="block">
              {dosage} {dosage_unit} {route} {frequency}{' '}
              {time && time_unit ? `For: ${time} ${time_unit} ` : ''}
              {index < dosing_instructions.length - 1 && connector}
            </span>
          );
        })}
      </div>
    );
  }

  return (
    <div className="hr-detail-card">
      <div className="hr-detail-card__section">
        <span>{name}</span>
        {quantity && (
          <div className="hr-detail-card__subsection">
            <span>Quantity:</span>
            <span>
              {quantity} {quantity_unit ?? ''}
            </span>
          </div>
        )}
        {refills && (
          <div className="hr-detail-card__subsection">
            <span>Refills:</span>
            <span>{refills}</span>
          </div>
        )}
        {lastsUntil && (
          <div className="hr-detail-card__subsection">
            <span>Lasts Until:</span>
            <span>{CalendarUtils.format(lastsUntil, 'MMMM Do YYYY')}</span>
          </div>
        )}
        {comments && (
          <div className="hr-detail-card__subsection">
            <span>Comments:</span>
            <span>{comments}</span>
          </div>
        )}
        {printDosingInstructions(workingMedication)}
      </div>
    </div>
  );
};

export const AttachmentBadge = ({ type, name }: { type: string; name: string }) => {
  return (
    <div className="attachment-badge">
      <Icon src={['fas', 'file-pdf']} /> {name}
    </div>
  );
};

export const ImmunizationItems = ({ immunization }: { immunization: Immunization }) => {
  const { given_date, scheduled_date, name, description, given_by, prescription, route, site } =
    immunization.value;

  return (
    <>
      {/* <Header date={prescription.date} filename={prescription.attachments && 'Prescription'}/> */}
      <div className="hr-detail-card">
        <div className="hr-detail-card__section">
          <span>{name}</span>
          <span>{description}</span>
          {given_by && (
            <div className="hr-detail-card__subsection">
              <span>Given by:</span>
              <span>{given_by}</span>
            </div>
          )}
          {given_date && (
            <div className="hr-detail-card__subsection">
              <span>Given on:</span>
              <span>{CalendarUtils.format(given_date, 'hh:mm A YYYY-MM-DD')}</span>
            </div>
          )}
          {!given_date && scheduled_date && (
            <div className="hr-detail-card__subsection">
              <span>Due by:</span>
              <span>{CalendarUtils.format(scheduled_date, 'YYYY-MM-DD')}</span>
            </div>
          )}
          {prescription && (
            <div className="hr-detail-card__subsection">
              <span>Prescription:</span>
              <span>{prescription}</span>
            </div>
          )}
          {route && (
            <div className="hr-detail-card__subsection">
              <span>Route:</span>
              <span>{route}</span>
            </div>
          )}
          {site && (
            <div className="hr-detail-card__subsection">
              <span>Site:</span>
              <span>{site}</span>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

async function decompressHtml(base64: string): Promise<string> {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  const ds = new DecompressionStream('gzip');
  const writer = ds.writable.getWriter();
  writer.write(bytes);
  writer.close();
  const reader = ds.readable.getReader();
  const chunks: Uint8Array[] = [];
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
  }
  return new TextDecoder().decode(await new Blob(chunks as any).arrayBuffer());
}

export const HtmlReportViewer = ({ html }: { html: string }) => {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [reportCss, setReportCss] = useState<string | null>(null);
  const [decompressedHtml, setDecompressedHtml] = useState<string | null>(null);
  const iframeReady = useRef(false);

  useEffect(() => {
    decompressHtml(html)
      .then(setDecompressedHtml)
      .catch(() => setDecompressedHtml(html));
  }, [html]);

  useEffect(() => {
    if (decompressedHtml === null) return;
    const variant = detectReportVariant(decompressedHtml);
    fetchReportCss(variant)
      .then(setReportCss)
      .catch(() => setReportCss(''));
  }, [decompressedHtml]);

  const iframeRefCallback = useCallback(
    (el: HTMLIFrameElement | null) => {
      iframeRef.current = el;
      if (!el || iframeReady.current || reportCss === null || decompressedHtml === null) return;
      const doc = el.contentDocument;
      if (!doc) return;
      doc.open();
      doc.write(`<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<link href="https://fonts.googleapis.com/css2?family=Montserrat:wght@400;600;700&family=Noto+Color+Emoji&display=swap" rel="stylesheet">
<style>${reportCss}</style>
</head>
<body>${decompressedHtml}</body>
</html>`);
      doc.close();
      iframeReady.current = true;
    },
    [reportCss, decompressedHtml],
  );

  if (reportCss === null || decompressedHtml === null) return null;

  return (
    <iframe
      ref={iframeRefCallback}
      className="html-report-viewer"
      sandbox="allow-same-origin"
      title="Report"
    />
  );
};

export const DocumentItems = ({ document }: { document: PatientDocument }) => {
  const nav = useNavStack(Stacks.HR);
  const masterNav = useNavStack(Stacks.Master);
  const authentication = useAppSelector((state) => state.authentication);
  const { title, comments, referral, filename } = document;
  const isExpired = isDocumentExpired(document, authentication);
  const isLocked = DocumentUtils.isLockedForPatient(document, authentication.role);
  const unlockDateText = isLocked ? DocumentUtils.getUnlockDateText(document) : '';
  const [isLoading, setLoading] = useState(false);
  const [showDocumentPreview, setShowDocumentPreview] = useState(false);
  const dispatch = useAppDispatch();

  const isPhone = isMobileDevice();
  const isNative = Device.isMobile();
  const showDownloadButton = isPhone && !isNative;
  const showOpenNative = isNative;
  const showOpenEmbedded = !showDownloadButton && !showOpenNative;

  const hasHtmlReport = !!document.html_viewable && !!document.html;

  const downloadOrOpenNative = () => {
    if (!filename) {
      return;
    }
    setLoading(true);
    downloadBlob(
      api.Documents.getDocumentFile,
      { documentId: document.id! },
      { filename: filename },
    ).finally(() => setLoading(false));
  };

  const openDocumentEmbedded = () => {
    setShowDocumentPreview(true);
  };

  function getFile<T extends DispatchOptions>(dispatchOptions: T) {
    return dispatch(api.Documents.getDocumentFile({ documentId: document?.id! }), dispatchOptions);
  }

  function getFileInfo() {
    return dispatch(api.Documents.getDocumentFileInfo({ documentId: document?.id! }));
  }

  function fillDocument() {
    if (!document.pending_patient_fill || !document) {
      return;
    }
    masterNav.push(`/patient-fill/${document.id}`);
  }

  function onClickedResult(value: { test: string | number | boolean }) {
    let url = `/metrics/Lab/${encodeURIComponent(value.test)}`;
    nav.push(url);
  }

  const hasDiagnosticResults = (document.results_text?.length || 0) > 0;

  return (
    <>
      {hasHtmlReport && <HtmlReportViewer html={document.html!} />}
      <div className="hr-detail-card">
        <div className="hr-detail-card__section">
          <span>{title}</span>
          {referral?.send_to && (
            <div className="hr-detail-card__subsection">
              <span>Send To:</span>
              <span>
                {referral?.send_to?.name} ({referral?.send_to?.specialty})
              </span>
            </div>
          )}
          {comments && (
            <div className="hr-detail-card__subsection">
              <span>Notes:</span>
              <span>{comments}</span>
            </div>
          )}
        </div>
      </div>
      <Header
        practitionerId={document.physician_id}
        date={document.date}
        filename={isExpired || isLocked ? undefined : document.title}
      />
      {hasDiagnosticResults && (
        <DiagnosticResults
          document={document}
          allowCopy={false}
          normalInitialValue={false}
          onClick={onClickedResult}
        />
      )}
      <div className="buttons">
        {!isExpired && !isLocked && filename && (
          <>
            {document.pending_patient_fill && (
              <DefaultButton name="Fill Document" loading={isLoading} onClick={fillDocument} />
            )}
            {showOpenEmbedded && !hasHtmlReport && (
              <DefaultButton
                name="Open File"
                className={document.pending_patient_fill ? 'light' : ''}
                onClick={openDocumentEmbedded}
                loading={isLoading}
              />
            )}
            {(showOpenNative || showDownloadButton || hasHtmlReport) && (
              <DefaultButton
                name="Download PDF"
                className={document.pending_patient_fill || hasHtmlReport ? 'light' : ''}
                onClick={downloadOrOpenNative}
                loading={isLoading}
              />
            )}
          </>
        )}
        {isExpired && <DefaultButton name="This requisition has expired" disabled />}
        {!isExpired && isLocked && (
          <DefaultButton name={`Available on ${unlockDateText}`} disabled />
        )}
      </div>
      {showDocumentPreview && (
        <DefaultModal
          className="modal health-record-modal large-modal"
          wrap={true}
          padding={false}
          content={
            //@ts-ignore
            <LargeResourcePreview
              autoLoad={!isLocked}
              getFile={getFile}
              getFileInfo={getFileInfo}
              locked={isLocked}
              unlockDateText={unlockDateText}
            />
          }
          isOpen={showDocumentPreview}
          onRequestClose={() => setShowDocumentPreview(false)}
        />
      )}
    </>
  );
};

// - TODO other healthmetrics
// - TODO METRICS
// - TODO documents
export const AppointmentNoteItems = ({ note }: { note: AppointmentNote }) => {
  const isAttachment = note.item_type === AppointmentNoteItemTypeEnum.Attachment;
  const [loading, setLoading] = useState(false);

  const onOpenAttachment = () => {
    setLoading(true);
    downloadBlob(
      api.HealthRecords.getNotesAttachment,
      { appointmentNoteId: note.id! },
      { filename: note.value.value },
    ).finally(() => setLoading(false));
  };

  return (
    <>
      <Header
        practitionerId={note.author_id}
        date={note.value.date}
        filename={isAttachment ? 'Attachment' : undefined}
      />
      {!isAttachment && note.value.value && (
        <div className="hr-detail-card">
          <div className="hr-detail-card__section">
            <span>Notes:</span>
            <pre>{note.value.value.trim()}</pre>
          </div>
        </div>
      )}
      {isAttachment && (
        <div className="buttons">
          <DefaultButton name="Open Attachment" onClick={onOpenAttachment} loading={loading} />
        </div>
      )}
    </>
  );
};

function isDocumentExpired(document: PatientDocument, authentication: AuthState) {
  return (
    document.document_type === DocumentUtils.DOCUMENT_TYPE.diagnostic_requisition &&
    moment
      .unix(document.date! + authentication.settings!.diagnostic_requisition_timeout!)
      .isBefore(moment().unix())
  );
}

function isMobileDevice() {
  return 'ontouchstart' in document.documentElement;
}
